import { DeviceKind, DeviceLite, DeviceSnapshot } from './shared';

// The only place that calls enumerateDevices or probes getUserMedia for label access.
export class DeviceRegistry {
    async checkMicPermission(): Promise<PermissionState | 'unknown'> {
        try {
            const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
            return status.state;
        } catch {
            return 'unknown';
        }
    }

    // On Android, a bare getUserMedia({audio:true}) resets the audio session to speakerphone,
    // overriding routing established by the active stream. Labels stay unlocked for the whole
    // session after the first grant, so the probe only runs when permission is not yet granted
    // (at which point no stream can be active either).
    private async unlockLabelsIfNeeded(): Promise<void> {
        const permission = await this.checkMicPermission();
        if (permission === 'granted') return;
        try {
            const probe = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            probe.getTracks().forEach(t => t.stop());
        } catch (e) {
            console.error('unlockLabelsIfNeeded', e);
        }
    }

    async enumerate(opts?: { ensureLabels?: boolean }): Promise<DeviceSnapshot> {
        if (opts?.ensureLabels) await this.unlockLabelsIfNeeded();
        const devices = await navigator.mediaDevices.enumerateDevices();
        const toLite = (d: MediaDeviceInfo): DeviceLite => ({
            deviceId: d.deviceId,
            groupId: d.groupId,
            kind: d.kind as DeviceKind,
            label: d.label
        });
        const inputs = devices.filter(d => d.kind === 'audioinput').map(toLite);
        const outputs = devices.filter(d => d.kind === 'audiooutput').map(toLite);
        return {
            inputs,
            outputs,
            defaultInputLabel: inputs.find(d => d.deviceId === 'default')?.label ?? '',
            defaultOutputLabel: outputs.find(d => d.deviceId === 'default')?.label ?? ''
        };
    }
}
